import styled from 'styled-components'
import {ComponentProps} from 'react'
import {system} from 'styled-system'
import {get} from '@fuegokit/react/constants'
import sx from '@fuegokit/react/sx'
import {StyledLinkProps} from './types'

const hoverColor = system({
  hoverColor: {
    property: 'color',
    scale: 'colors'
  }
})

/**
 * Use the Link component to style an anchor tag with anchor tags with platform hyperlink color cues and hover text decoration,
 * while also respecting accessibility.
 *
 * When choosing a link or a button, links are used for destinations, whenever the action may refresh the browers,
 * or when moving from one page to another.
 *
 * The Link component can be used as a semantic `button`, by using `<Link as='button' onClick={your onClick here}>.
 *
 * If you use the `as` prop, be sure to pass the inclusive and semantically correct element, such as an `a`, `button`, or
 * `summary` tag.
 */
const Link = styled.a<StyledLinkProps>`
  color: ${p => (p.subtle ? get('colors.text.subtlest') : get('colors.link.default'))};
  text-decoration: ${p => (p.underline ? 'underline' : 'none')};
  font-weight: ${get('fontWeights.semibold')};
  &:hover {
    cursor: pointer;
    background: none;
    text-decoration: underline;
    transition-duration: 0s, 0.15s;
    ${p => (p.hoverColor ? hoverColor : p.subtle ? `${get('colors.text.subtlest')}` : '')}
  }

  &:active {
    background: none;
    transition-duration: 0s;
    color: ${get('colors.link.pressed')};
  }
  &:focus {
    background: none;
    box-shadow: 0 0 0 2px ${get('colors.border.focused')};
    transition-duration: 0s, 0.2s;
    color: ${get('colors.text.subtle')};
  }
  /* &:visited {
    color: ${p => (p.subtle ? get('colors.text.subtlest') : get('colors.link.default'))};
  } */
  &:is(button) {
    display: inline-block;
    padding: 0;
    margin: 0;
    font-size: inherit;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 0;
    appearance: none;
  }
  ${sx}
`

export type LinkProps = ComponentProps<typeof Link>
export default Link
